import React from "react";
import { searchService } from "../services/searchService";
import { useOrg } from "./OrganizationContext";
import { geocode, getServiceArea } from "../utils/geo";

const DEFAULT_PER_PAGE = 25;

const initialSearchState = {
  terms: [],
  location: "",
  service_area: "",
  coords: null,
  page: 1,
  per_page: DEFAULT_PER_PAGE,
  radius: null,
  taxonomy_code: [],
  combined_taxonomy_ids: [],
  tp: null,
  gender: null,
  age_range: null,
  open_now: null,
  open_on: [],
  area_sort: null,
  referral: null,
  response_id: null,
  category: null,
  survey_name: null,
  token: null,
};

const SearchContext = React.createContext({
  params: initialSearchState,
  results: [],
  relatedTerms: [],
  targetPopulations: [],
  categorySelections: [],
  notices: {},
  totalResults: 0,
  lastPage: 1,
  loading: false,
  error: null,
  search: () => Promise.resolve(),
  loadPage: () => {},
  setParams: () => {},
  resetFilters: () => {},
  clearAll: () => {},
  resultForId: () => null,
  toUrl: () => "",
  fromUrl: () => {},
});
SearchContext.displayName = "SearchContext";

function searchReducer(state, action) {
  switch (action.type) {
    case "SET_PARAMS":
      return { ...state, params: { ...state.params, ...action.payload } };
    case "REPLACE_PARAMS":
      return { ...state, params: { ...initialSearchState, ...action.payload } };
    case "SEARCH_START":
      return { ...state, loading: true, error: null, categorySelections: [] };
    case "SEARCH_SUCCESS": {
      const { results, related_terms, target_populations, notices, taxonomies, total_results, page, ...rest } = action.payload;
      const lastPage = Math.ceil(total_results / (state.params.per_page || DEFAULT_PER_PAGE));
      const categorySelections = [...state.categorySelections];
      if (taxonomies) {
        const existingNames = categorySelections.map((c) => c.name);
        taxonomies.forEach((item) => {
          if (!existingNames.includes(item.name)) {
            categorySelections.push({ id: item.id, name: item.name });
          }
        });
      }
      return {
        ...state,
        results: results || [],
        relatedTerms: related_terms || [],
        targetPopulations: target_populations || [],
        notices: notices || {},
        categorySelections,
        totalResults: total_results || 0,
        lastPage,
        params: { ...state.params, page: parseInt(page) || state.params.page, ...rest },
        loading: false,
      };
    }
    case "SEARCH_ERROR":
      return { ...state, loading: false, error: action.payload };
    case "CLEAR_ALL":
      return {
        ...state,
        params: { ...initialSearchState },
        results: [],
        relatedTerms: [],
        targetPopulations: [],
        categorySelections: [],
        notices: {},
        totalResults: 0,
        lastPage: 1,
      };
    case "RESET_FILTERS":
      return {
        ...state,
        params: {
          ...state.params,
          radius: null,
          tp: null,
          gender: null,
          age_range: null,
          open_now: null,
          open_on: [],
          area_sort: null,
          referral: null,
        },
      };
    case "SET_CATEGORY_SELECTIONS":
      return { ...state, categorySelections: action.payload };
    default:
      return state;
  }
}

function toUrlParams(params) {
  const query = {};
  if (params.terms && params.terms.length) query.terms = params.terms.join(",");
  if (params.page) query.page = params.page;
  if (params.location) query.location = params.location;
  if (hasValidCoords(params.coords)) {
    query.lat = params.coords.lat;
    query.lng = params.coords.lng;
  }
  if (params.taxonomy_code && params.taxonomy_code.length) query.taxonomy_code = params.taxonomy_code.join(",");
  if (params.combined_taxonomy_ids && params.combined_taxonomy_ids.length) query.combined_taxonomy_ids = params.combined_taxonomy_ids.join(",");
  if (params.radius != null) query.radius = params.radius;
  if (params.service_area) query.service_area = params.service_area;
  if (params.tp) query.tp = params.tp;
  if (params.gender) query.gender = params.gender;
  if (params.age_range) query.age_range = params.age_range;
  if (params.open_now) query.open_now = params.open_now;
  if (params.open_on && params.open_on.length) query.open_on = params.open_on.join(",");
  if (params.area_sort) query.area_sort = params.area_sort;
  if (params.response_id) query.response_id = params.response_id;
  if (params.category) query.category = params.category;
  if (params.survey_name) query.survey_name = params.survey_name;
  if (params.token) query.token = params.token;
  if (params.referral) query.referral = params.referral;

  const qs = Object.entries(query)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return `/search?${qs}`;
}

function hasValidCoords(coords) {
  return coords?.lat != null && coords?.lng != null;
}

function parseCoordParam(value) {
  const coord = Number.parseFloat(value);
  return Number.isNaN(coord) ? null : coord;
}

function parseUrlParams(searchString) {
  const urlParams = new URLSearchParams(searchString);
  const parsed = {};
  const arrayKeys = ["taxonomy_code", "combined_taxonomy_ids", "tp", "open_on", "terms"];
  const intKeys = ["page", "radius"];

  for (const [key, value] of urlParams.entries()) {
    if (key === "coords") {
      // coords is an object that can't be serialized as a URL param; skip it
      continue;
    } else if (key === "view") {
      // The results view is UI state, not an API search filter.
      continue;
    } else if (key === "lat" || key === "lng") {
      const coord = parseCoordParam(value);
      if (coord != null) {
        if (!parsed.coords) parsed.coords = {};
        parsed.coords[key] = coord;
      }
    } else if (key === "term") {
      // Legacy support: "term" -> "terms"
      parsed.terms = value.split(",");
    } else if (key === "taxonomy_code") {
      // taxonomy_code values are integer DB IDs; parse them as numbers to match
      // what the API returns in categorySelections and what suggestion selection stores
      parsed[key] = value.split(",").map((v) => {
        const n = parseInt(v, 10);
        return isNaN(n) ? v : n;
      });
    } else if (arrayKeys.includes(key)) {
      parsed[key] = value.split(",");
    } else if (intKeys.includes(key)) {
      parsed[key] = parseInt(value, 10);
    } else {
      parsed[key] = value;
    }
  }

  if (!hasValidCoords(parsed.coords)) delete parsed.coords;

  return parsed;
}

function SearchProvider({ children }) {
  const { organization } = useOrg();
  const [state, dispatch] = React.useReducer(searchReducer, {
    params: { ...initialSearchState },
    results: [],
    relatedTerms: [],
    targetPopulations: [],
    categorySelections: [],
    notices: {},
    totalResults: 0,
    lastPage: 1,
    loading: false,
    error: null,
  });

  const search = React.useCallback(
    async (overrides = {}, { reset = false } = {}) => {
      const base = reset ? initialSearchState : state.params;
      const merged = { ...base, ...overrides };
      dispatch({ type: reset ? "REPLACE_PARAMS" : "SET_PARAMS", payload: overrides });
      dispatch({ type: "SEARCH_START" });
      try {
        // If no coords but location is present, geocode first (matches original Marionette behavior)
        if (!hasValidCoords(merged.coords) && merged.location && typeof google !== "undefined" && organization.serviceLocale) {
          try {
            const results = await geocode(merged.location, organization);
            const position = results[0].geometry.location;
            merged.coords = { lng: position.lng(), lat: position.lat() };
            // Only set service_area from geocoder if not already set from user input
            if (!merged.service_area || merged.service_area === (organization.serviceLocale.stateName || "").toLowerCase()) {
              const serviceArea = getServiceArea(results);
              if (serviceArea) {
                merged.service_area = serviceArea.toLowerCase();
              }
            }
            dispatch({ type: "SET_PARAMS", payload: { coords: merged.coords, service_area: merged.service_area } });
          } catch (geoErr) {
            // Geocoding failed; search proceeds without updated coords/service_area
          }
        }
        const data = await searchService.search(merged);
        dispatch({ type: "SEARCH_SUCCESS", payload: data });
        sessionStorage.setItem("lastSearchUrl", toUrlParams(merged));
        return data;
      } catch (err) {
        dispatch({ type: "SEARCH_ERROR", payload: err });
        throw err;
      }
    },
    [state.params, organization]
  );

  const loadPage = React.useCallback(
    (page) => {
      let nextPage;
      if (page === "next" && state.params.page < state.lastPage) {
        nextPage = state.params.page + 1;
      } else if (page === "previous" && state.params.page > 1) {
        nextPage = state.params.page - 1;
      } else if (typeof page === "number") {
        nextPage = page;
      }
      if (nextPage != null && nextPage !== state.params.page) {
        search({ page: nextPage });
      }
    },
    [state.params.page, state.lastPage, search]
  );

  const setParams = React.useCallback((updates) => {
    dispatch({ type: "SET_PARAMS", payload: updates });
  }, []);

  const resetFilters = React.useCallback(() => {
    dispatch({ type: "RESET_FILTERS" });
  }, []);

  const clearAll = React.useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const resultForId = React.useCallback(
    (id) => state.results.find((r) => r.program_site_id === parseInt(id, 10)) || null,
    [state.results]
  );

  const value = React.useMemo(
    () => ({
      ...state,
      search,
      loadPage,
      setParams,
      resetFilters,
      clearAll,
      resultForId,
      toUrl: () => toUrlParams(state.params),
      fromUrl: (searchString) => {
        const parsed = parseUrlParams(searchString);
        dispatch({ type: "SET_PARAMS", payload: parsed });
        return parsed;
      },
    }),
    [state, search, loadPage, setParams, resetFilters, clearAll, resultForId]
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

function useSearch() {
  return React.useContext(SearchContext);
}

export { SearchContext, SearchProvider, useSearch, toUrlParams, parseUrlParams };
